
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Loader2, ArrowRight, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { SubscriptionPlan } from '@/types/subscription';
import Header from '@/components/Header';
import CreditRefundForm from '@/components/CreditRefundForm';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

const Pricing: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isYearly, setIsYearly] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, hasActiveSubscription } = useAuth();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-subscription-plans', {
          method: 'GET'
        });
        
        if (error) {
          throw error;
        }
        
        setPlans(data || []);
      } catch (error) {
        console.error('Error fetching plans:', error);
        toast({
          title: 'Fehler',
          description: 'Abonnementpläne konnten nicht geladen werden.',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPlans();
  }, [toast]);

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    setLoadingPlan(plan.id);
    
    try {
      const priceId = isYearly ? plan.stripe_price_yearly_id : plan.stripe_price_monthly_id;
      
      if (!priceId) {
        toast({
          title: 'Nicht verfügbar',
          description: `${isYearly ? 'Jährliche' : 'Monatliche'} Abrechnung nicht verfügbar für diesen Plan.`,
          variant: 'destructive'
        });
        return;
      }
      
      const { data, error } = await supabase.functions.invoke(
        'create-checkout-session',
        {
          body: {
            priceId,
            planType: 'subscription',
            mode: 'subscription'
          }
        }
      );
      
      if (error) {
        throw error;
      }
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: 'Fehler',
        description: 'Checkout konnte nicht erstellt werden.',
        variant: 'destructive'
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const formatPrice = (price: number) => {
    return (price / 100).toFixed(2).replace('.', ',') + ' CHF';
  };

  const handleRequestEnterprise = () => {
    navigate('/contact', { state: { subject: 'Enterprise Plan Anfrage' } });
  };

  // Filter and organize plans
  const basicPlan = plans.find(plan => plan.name === 'Basic');
  const proPlan = plans.find(plan => plan.name === 'Pro');
  const enterprisePlan = plans.find(plan => plan.name === 'Enterprise');
  
  const displayPlans = [basicPlan, proPlan, enterprisePlan].filter(Boolean) as SubscriptionPlan[];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-black to-gray-900 text-white">
      <Header />
      <ScrollArea className="flex-1 w-full">
        <div className="container mx-auto px-4 py-12 pt-32">
          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold mb-4">Preise & Pakete</h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Wählen Sie den passenden Plan für Ihre Bedürfnisse und generieren Sie professionelle E-Mails für Ihre Versicherungskunden.
            </p>
            
            <div className="mt-4">
              <Button 
                variant="outline" 
                className="border-gray-600 text-gray-400 flex items-center"
                onClick={() => navigate('/stripe-setup')}
              >
                <Settings className="h-4 w-4 mr-2" />
                Stripe Setup
              </Button>
            </div>
            
            <div className="mt-8 inline-flex items-center bg-gray-800/50 p-1 rounded-lg border border-gray-700">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition ${!isYearly ? 'bg-jade text-white' : 'text-gray-400'}`}
                onClick={() => setIsYearly(false)}
              >
                Monatlich
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium transition ${isYearly ? 'bg-jade text-white' : 'text-gray-400'}`}
                onClick={() => setIsYearly(true)}
              >
                Jährlich <Badge className="ml-1 bg-amber-400 text-black">-20%</Badge>
              </button>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-jade" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-12">
              {displayPlans.map((plan, index) => {
                const isPopular = plan?.name === 'Pro';
                const isEnterprise = plan?.name === 'Enterprise';
                const isSubscribeLoading = loadingPlan === plan?.id;
                
                // Custom credit amounts based on plan
                let creditAmount = isYearly ? plan?.credits : Math.round((plan?.credits || 0) / 12);
                if (plan?.name === 'Basic') creditAmount = isYearly ? 50 : 4;
                if (plan?.name === 'Pro') creditAmount = isYearly ? 150 : 12;
                
                return (
                  <Card 
                    key={plan?.id || index}
                    className={`bg-gray-800/20 border-gray-700 text-white h-full ${isPopular ? 'border-jade ring-2 ring-jade/20 transform md:scale-105' : ''}`}
                  >
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-xl">{isEnterprise ? 'Corporate' : plan?.name}</CardTitle>
                        {isPopular && (
                          <Badge className="bg-jade text-white">Beliebt</Badge>
                        )}
                      </div>
                      <CardDescription className="text-gray-400">
                        {plan?.name === 'Basic' ? 'Perfect for occasional users' : 
                         plan?.name === 'Pro' ? 'For frequent users' : 
                         'For teams and businesses'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-6">
                        <div className="text-3xl font-bold">
                          {isEnterprise ? 'Auf Anfrage' : formatPrice(isYearly ? (plan?.price_yearly || 0) / 12 : (plan?.price_monthly || 0))}
                        </div>
                        {!isEnterprise && !isYearly && (
                          <div className="text-gray-400 text-sm">
                            pro Monat
                          </div>
                        )}
                        {!isEnterprise && isYearly && (
                          <div className="text-amber-400 text-sm">
                            pro Monat bei jährlicher Zahlung
                          </div>
                        )}
                        {isYearly && !isEnterprise && (
                          <div className="text-sm text-gray-400 mt-1">
                            {formatPrice(plan?.price_yearly || 0)} pro Jahr
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-3 mt-6">
                        <div className="flex items-center">
                          <Check className="h-5 w-5 text-jade mr-2 flex-shrink-0" />
                          <span>
                            {isEnterprise ? 'Individuelle Credits' : `${creditAmount} Credits`} 
                            {!isEnterprise && (isYearly ? ' pro Jahr' : ' pro Monat')}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <Check className="h-5 w-5 text-jade mr-2 flex-shrink-0" />
                          <span>1 Credit pro E-Mail oder Dokument</span>
                        </div>
                        <div className="flex items-center">
                          <Check className="h-5 w-5 text-jade mr-2 flex-shrink-0" />
                          <span>E-Mail-Generation</span>
                        </div>
                        <div className="flex items-center">
                          <Check className="h-5 w-5 text-jade mr-2 flex-shrink-0" />
                          <span>Dokumentenanalyse</span>
                        </div>
                        <div className="flex items-center">
                          <Check className="h-5 w-5 text-jade mr-2 flex-shrink-0" />
                          <span>Prioritäts-Support</span>
                        </div>
                        {(plan?.name === 'Pro' || isEnterprise) && (
                          <div className="flex items-center">
                            <Check className="h-5 w-5 text-jade mr-2 flex-shrink-0" />
                            <span>Erweiterte Anpassung</span>
                          </div>
                        )}
                        {plan?.name === 'Pro' && (
                          <div className="flex items-center">
                            <Check className="h-5 w-5 text-jade mr-2 flex-shrink-0" />
                            <span>Zugang zu neuen AI-Agenten (Beta)</span>
                          </div>
                        )}
                        {isEnterprise && (
                          <>
                            <div className="flex items-center">
                              <Check className="h-5 w-5 text-jade mr-2 flex-shrink-0" />
                              <span>Mehrere Benutzerkonten</span>
                            </div>
                            <div className="flex items-center">
                              <Check className="h-5 w-5 text-jade mr-2 flex-shrink-0" />
                              <span>Individuelle Anpassungen</span>
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                    <CardFooter className="mt-auto">
                      {isEnterprise ? (
                        <Button 
                          onClick={handleRequestEnterprise}
                          className="w-full bg-gray-700 hover:bg-gray-600 text-white"
                        >
                          Kontaktieren Sie uns
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => plan && handleSubscribe(plan)}
                          disabled={isSubscribeLoading || (hasActiveSubscription && !isEnterprise)}
                          className={`w-full ${isPopular ? 'bg-jade hover:bg-jade/90' : ''}`}
                          variant={isPopular ? 'default' : 'outline'}
                        >
                          {isSubscribeLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : hasActiveSubscription ? (
                            'Bereits abonniert'
                          ) : (
                            'Jetzt abonnieren'
                          )}
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
          
          <div className="mt-8 mb-16 bg-gray-800/30 border border-gray-700 p-8 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold mb-4">Häufig gestellte Fragen</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Was sind Credits?</h4>
                    <p className="text-gray-400">
                      Credits sind die Währung in unserem System. Jede E-Mail-Generierung oder Dokumentenanalyse kostet 1 Credit.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Was passiert, wenn ich keine Credits mehr habe?</h4>
                    <p className="text-gray-400">
                      Sie können jederzeit zusätzliche Credits kaufen oder auf ein höheres Abonnement upgraden, um mehr Credits zu erhalten.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Kann ich mein Abonnement kündigen?</h4>
                    <p className="text-gray-400">
                      Ja, Sie können Ihr Abonnement jederzeit kündigen. Es läuft dann bis zum Ende der aktuellen Abrechnungsperiode.
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-4">Zusätzliche Credits</h3>
                <p className="text-gray-400 mb-4">
                  Benötigen Sie mehr Credits? Sie können jederzeit zusätzliche Credit-Pakete kaufen, unabhängig von Ihrem Abonnement.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    onClick={() => navigate('/account')}
                    className="bg-jade hover:bg-jade/90 text-white"
                  >
                    Credits kaufen
                  </Button>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-gray-600">
                        Problem mit Credits melden
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-gray-800 border-gray-700 text-white">
                      <DialogHeader>
                        <DialogTitle>Credit-Problem melden</DialogTitle>
                        <DialogDescription className="text-gray-400">
                          Wenn Sie ein Problem mit Ihren Credits haben oder eine Rückerstattung beantragen möchten, füllen Sie das folgende Formular aus.
                        </DialogDescription>
                      </DialogHeader>
                      <CreditRefundForm />
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default Pricing;
